import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { getAuthToken, getRefreshToken, setAuthToken, setRefreshToken, clearAuthData } from '@/utils/auth';
import { LoginCredentials, AuthResponse, RefreshTokenResponse } from '../types/auth';

// Import API configuration
import { API_CONFIG } from '@/config/api.config';

// Use the base URL from API config
const API_BASE_URL = API_CONFIG.BASE_URL.replace(/\/$/, '');

console.log('API Base URL:', API_BASE_URL); // For debugging

class ApiService {
  private api: AxiosInstance;
  private isRefreshing = false;
  private failedQueue: Array<{
    resolve: (token: string) => void;
    reject: (error: AxiosError) => void;
  }> = [];

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor
    this.api.interceptors.request.use(
      async (config: InternalAxiosRequestConfig) => {
        // Skip adding auth header for auth endpoints
        const isAuthEndpoint = config.url?.includes('/auth/');
        
        if (isAuthEndpoint) {
          console.log('[API] Skipping auth header for auth endpoint:', config.url);
          return config;
        }

        // Get the token with validation
        const token = getAuthToken();
        
        console.log('[API] Request:', {
          url: config.url,
          method: config.method?.toUpperCase(),
          hasToken: !!token,
          tokenLength: token?.length,
          tokenPreview: token ? `${token.substring(0, 5)}...${token.substring(token.length - 5)}` : 'none',
          isAuthEndpoint,
          timestamp: new Date().toISOString()
        });
        
        if (token) {
          // Ensure headers object exists
          config.headers = config.headers || {};
          
          // Add Authorization header
          const authHeader = `Bearer ${token}`;
          config.headers.Authorization = authHeader;
          
          // Log detailed token info
          console.log('[API] Added Authorization header:', {
            headerLength: authHeader.length,
            tokenStartsWith: token.substring(0, 10),
            tokenEndsWith: token.substring(token.length - 10),
            tokenParts: token.split('.').map((part, i) => ({
              part: i,
              length: part.length,
              preview: i < 2 ? `${part.substring(0, 5)}...` : part
            }))
          });
        } else {
          console.warn('[API] No valid auth token found', {
            hasToken: !!token,
            storageKeys: Object.keys(localStorage)
          });
          
          // Don't proceed with the request if we don't have a valid token
          // unless it's a public endpoint
          if (!config.url?.includes('/public/')) {
            console.error('[API] Aborting request - no valid token available');
            return Promise.reject(new Error('Authentication required'));
          }
        }
        
        return config;
      },
      (error) => {
        console.error('[API] Request interceptor error:', {
          message: error.message,
          config: error.config,
          url: error.config?.url
        });
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.api.interceptors.response.use(
      (response) => {
        console.log('[API] Response:', {
          status: response.status,
          url: response.config.url,
          method: response.config.method?.toUpperCase(),
          data: response.data ? '[...data]' : 'no data'
        });
        return response;
      },
      async (error: AxiosError) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
        
        // Log the error
        console.error('[API] Response Error:', {
          status: error.response?.status,
          url: originalRequest.url,
          method: originalRequest.method,
          path: originalRequest.url?.replace(API_BASE_URL, ''),
          isAuthRequest: originalRequest.url?.includes('/auth/'),
          _retry: originalRequest._retry
        });
        
        // If error is not a 401 or this is an auth request, reject immediately
        if (error.response?.status !== 401 || originalRequest.url?.includes('/auth/')) {
          return Promise.reject(error);
        }
        
        // If we've already tried to refresh, reject
        if (originalRequest._retry) {
          console.log('[API] Already attempted token refresh, logging out...');
          // Clear auth data and redirect to login
          clearAuthData();
          if (typeof window !== 'undefined') {
            window.location.href = '/login';
          }
          return Promise.reject(new Error('Session expired. Please log in again.'));
        }
        
        // Mark that we're retrying this request
        originalRequest._retry = true;
        
        // If we're already refreshing the token, add to queue
        if (this.isRefreshing) {
          console.log('[API] Token refresh in progress, adding to queue');
          return new Promise<AxiosResponse>((resolve, reject) => {
            this.failedQueue.push({ 
              resolve: (token: string) => {
                console.log('[API] Processing queued request with new token');
                originalRequest.headers = originalRequest.headers || {};
                originalRequest.headers.Authorization = `Bearer ${token}`;
                this.api(originalRequest).then(resolve).catch(reject);
              },
              reject: (err: AxiosError) => {
                console.error('[API] Queue request failed:', err.message);
                reject(err);
              } 
            });
          });
        }

        // Mark that we're refreshing the token
        this.isRefreshing = true;
        console.log('[API] Starting token refresh process');

        try {
          // Get refresh token
          const refreshToken = getRefreshToken();
          if (!refreshToken) {
            throw new Error('No refresh token available');
          }
          
          console.log('[API] Refreshing access token...');
          
          // Make the refresh token request
          const response = await axios.post<RefreshTokenResponse>(
            `${API_CONFIG.ENDPOINTS.AUTH}/refresh-token`,
            { refreshToken },
            {
              baseURL: API_BASE_URL,
              headers: {
                'Content-Type': 'application/json',
              },
              withCredentials: true
            }
          );
          
          console.log('[API] Token refresh response:', {
            status: response.status,
            hasToken: !!response.data?.token,
            hasRefreshToken: !!response.data?.refreshToken
          });
          
          // Validate response
          if (!response.data?.token) {
            throw new Error('No token in refresh response');
          }
          
          // Update tokens in storage
          const { token: newToken, refreshToken: newRefreshToken } = response.data;
          setAuthToken(newToken);
          
          if (newRefreshToken) {
            setRefreshToken(newRefreshToken);
            console.log('[API] Updated refresh token');
          }
          
          console.log('[API] Successfully refreshed access token');
          
          // Update the original request with the new token
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
          }
          
          // Process any queued requests with the new token
          this.processQueue(null, newToken);
          
          // Retry the original request with the new token
          console.log('[API] Retrying original request with new token');
          return this.api(originalRequest);
          
        } catch (refreshError: any) {
          console.error('[API] Token refresh failed:', {
            message: refreshError.message,
            response: refreshError.response?.data,
            status: refreshError.response?.status
          });
          
          // Process any queued requests with the error
          this.processQueue(refreshError);
          
          // Clear auth data
          console.log('[API] Clearing auth data due to refresh failure');
          clearAuthData();
          
          // Redirect to login page
          if (typeof window !== 'undefined') {
            console.log('[API] Redirecting to login page');
            window.location.href = '/login';
          }
          
          return Promise.reject(refreshError);
          
        } finally {
          console.log('[API] Finished token refresh process');
          this.isRefreshing = false;
        }
      }
    );
  }

  private processQueue(error: AxiosError | null, token: string | null = null) {
    this.failedQueue.forEach((promise) => {
      if (error) {
        promise.reject(error);
      } else if (token) {
        promise.resolve(token);
      }
    });
    this.failedQueue = [];
  }

  // Auth methods
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      console.log('Attempting login with credentials:', {
        ...credentials,
        password: credentials.password ? '[HIDDEN]' : 'undefined'
      });
      
      // Make a direct axios request with explicit URL and headers
      const url = `${API_BASE_URL}${API_CONFIG.ENDPOINTS.AUTH}/login`;
      console.log('Making request to:', url);
      
      const response = await axios({
        method: 'post',
        url,
        data: credentials,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        withCredentials: true,
        validateStatus: () => true // This ensures we get the response even if status is not 2xx
      });
      
      console.log('Login response status:', response.status);
      console.log('Response headers:', JSON.stringify(response.headers, null, 2));
      
      if (response.status !== 200) {
        console.error('Login failed with status:', response.status);
        console.error('Response data:', response.data);
        throw new Error(response.data?.message || 'Login failed');
      }
      
      console.log('Login successful. Response data:', {
        ...response.data,
        token: response.data.token ? '[TOKEN_RECEIVED]' : 'undefined',
        refreshToken: response.data.refreshToken ? '[REFRESH_TOKEN_RECEIVED]' : 'undefined'
      });
      
      return response.data;
    } catch (error: any) {
      console.error('Login error details:', {
        message: error.message,
        response: error.response ? {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data
        } : 'No response',
        request: error.request ? 'Request made but no response' : 'No request made',
        config: error.config ? {
          url: error.config.url,
          method: error.config.method,
          headers: error.config.headers,
          data: error.config.data
        } : 'No config'
      });
      
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        throw new Error(error.response.data?.message || 'Login failed. Please check your credentials.');
      } else if (error.request) {
        // The request was made but no response was received
        throw new Error('No response from server. Please check your connection.');
      } else {
        // Something happened in setting up the request
        throw new Error(error.message || 'An error occurred during login');
      }
    }
  }

  async refreshToken(): Promise<RefreshTokenResponse> {
    console.log('[API] Starting token refresh');
    
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      const error = new Error('No refresh token available');
      console.error('[API] Refresh token error:', error.message);
      throw error;
    }

    try {
      console.log('[API] Sending refresh token request');
      const response = await axios.post<RefreshTokenResponse>(
        `${API_CONFIG.ENDPOINTS.AUTH}/refresh-token`,
        { refreshToken },
        {
          baseURL: API_BASE_URL,
          headers: {
            'Content-Type': 'application/json',
          },
          withCredentials: true
        }
      );

      console.log('[API] Token refresh successful');
      
      // Update tokens in localStorage
      if (response.data.token) {
        setAuthToken(response.data.token);
        console.log('[API] Updated access token');
      }
      
      if (response.data.refreshToken) {
        setRefreshToken(response.data.refreshToken);
        console.log('[API] Updated refresh token');
      }

      return response.data;
    } catch (error: any) {
      console.error('[API] Refresh token error:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      // Clear auth data on refresh token failure
      console.log('[API] Clearing auth data due to refresh token failure');
      clearAuthData();
      
      // Redirect to login page
      if (typeof window !== 'undefined') {
        window.location.href = '/login';
      }
      
      throw error;
    }
  }

  async logout(): Promise<void> {
    try {
      await this.api.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    } finally {
      clearAuthData();
    }
  }

  // Generic HTTP methods
  async get<T>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.api.get<T>(url, config);
  }

  async post<T>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.api.post<T>(url, data, config);
  }

  async put<T>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.api.put<T>(url, data, config);
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.api.delete<T>(url, config);
  }

  async patch<T>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.api.patch<T>(url, data, config);
  }
}

export const apiService = new ApiService();
